import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AppDispatch,
  RootState,
  getClient,
  bearerAuth,
  validationErrorsWithoutI18n,
  getErrorMessage,
  changeLocation,
} from '@src/core/store';
import {
  Buyer,
  LoginRequest,
  SignupRequest,
  ResetPasswordRequest,
  RecoverPasswordRequest,
  ValidatePasswordRecoveryCodeRequest,
  AgreementDocument,
  VerifyPhoneRequest,
  UserRequest,
  AppInfo,
} from '@src/core/buyerApiClient';
import { AxiosError, AxiosResponse } from 'axios';
import * as storage from '../common/storage';
import { track } from '@src/services/analytics';
import { dateWasReached } from '@src/common';
import { lockDateOn } from '@src/common/auth';
import { clearCartReference, setupCart } from './cart';

const planneWidgetBuyerKey = 'planneWidgetUser';
export type allowedCurrentTerms = 'terms_of_use' | 'privacy_policy' | null;

type PhoneVerificationStatus = 'inProgress' | 'error' | 'idle' | 'success';

interface AuthState {
  logged?: boolean;
  publicApiKey?: string;
  buyer?: Buyer;
  buyerToken?: string;
  termsData?: { [key: string]: AgreementDocument };
  loadingTerms: boolean;
  currentTerms: allowedCurrentTerms;
  errors: { [key: string]: { [key: string]: string[] } };
  flashMessage?: string;
  passwordRecoveryEmail?: string;
  pendingSignup: boolean;
  createdAccount: boolean;
  resendEmail: boolean;
  passwordRecoveryCode?: string;
  fixEmail: boolean;
  codeResent: boolean;
  modifiedPassword: boolean;
  sendingForm: boolean;
  setupAuthOngoing: boolean;
  phoneVerification: {
    status: PhoneVerificationStatus;
    loading: boolean;
    requestLockedUntil?: string;
  };
  otp: {
    status: PhoneVerificationStatus;
    loading: boolean;
    requestLockedUntil?: string;
  };
  resetPassword: {
    loading: boolean;
    isCodeValid: boolean;
    code: string | undefined;
    requestLockedUntil?: string;
  };
  appInfo?: AppInfo;
}

const initialState: AuthState = {
  currentTerms: null,
  errors: {},
  loadingTerms: false,
  createdAccount: false,
  pendingSignup: false,
  resendEmail: false,
  fixEmail: false,
  codeResent: false,
  modifiedPassword: false,
  sendingForm: false,
  setupAuthOngoing: true,
  phoneVerification: {
    status: 'idle',
    loading: false,
    requestLockedUntil: undefined,
  },
  otp: {
    status: 'idle',
    loading: false,
    requestLockedUntil: undefined,
  },
  resetPassword: {
    loading: false,
    isCodeValid: false,
    code: undefined,
    requestLockedUntil: undefined,
  },
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setPublicKey: (state: AuthState, action: PayloadAction<string, string>) => {
      state.publicApiKey = action.payload;
    },
    setBuyer: {
      reducer(state, action: PayloadAction<Buyer, string, { token: string; isNewAccount: boolean }>) {
        state.buyer = action.payload;
        state.buyerToken = action.meta.token;
        state.logged = true;
      },
      prepare(token: string, payload: Buyer, isNewAccount: boolean) {
        return { payload, meta: { token, isNewAccount } };
      },
    },
    clearBuyer: (state: AuthState) => {
      state.buyer = undefined;
      state.buyerToken = undefined;
      state.logged = false;
    },
    setTermsData: (state: AuthState, action: PayloadAction<{ [key: string]: AgreementDocument }, string>) => {
      state.termsData = action.payload;
    },
    setCurrentTerms: (state: AuthState, action: PayloadAction<allowedCurrentTerms>) => {
      state.currentTerms = action.payload;
    },
    setLoadingTerms: (state: AuthState, action: PayloadAction<boolean>) => {
      state.loadingTerms = action.payload;
    },
    setErrors: {
      reducer(state, action: PayloadAction<{ [key: string]: string[] }, string, { key: string }>) {
        state.errors[action.meta.key] = action.payload;
      },
      prepare(key: string, payload: { [key: string]: string[] }) {
        return { payload, meta: { key } };
      },
    },
    setFlashMessage: (state: AuthState, action: PayloadAction<string, string>) => {
      state.flashMessage = action.payload;
    },
    ackFlashMessage: (state: AuthState) => {
      state.flashMessage = undefined;
    },
    setPasswordRecoveryEmail: (state: AuthState, action: PayloadAction<string | undefined, string>) => {
      state.passwordRecoveryEmail = action.payload?.toLowerCase();
    },
    setPasswordRecoveryCode: (state: AuthState, action: PayloadAction<string | undefined, string>) => {
      state.passwordRecoveryCode = action.payload;
    },
    setLogged: (state: AuthState, action: PayloadAction<boolean, string>) => {
      state.logged = action.payload;
    },
    setSetupAuthOngoing: (state: AuthState, action: PayloadAction<boolean, string>) => {
      state.setupAuthOngoing = action.payload;
    },
    requestSignup: (state: AuthState) => {
      state.pendingSignup = true;
    },
    receiveSignup: (state: AuthState) => {
      state.pendingSignup = false;
    },
    setCreatedAccount: (state: AuthState, action: PayloadAction<boolean>) => {
      state.createdAccount = action.payload;
    },
    setResendEmail: (state: AuthState, action: PayloadAction<boolean>) => {
      state.resendEmail = action.payload;
    },
    setFixEmail: (state: AuthState, action: PayloadAction<boolean>) => {
      state.fixEmail = action.payload;
    },
    setCodeResent: (state: AuthState, action: PayloadAction<boolean>) => {
      state.codeResent = action.payload;
    },
    setmodifiedPassword: (state: AuthState, action: PayloadAction<boolean>) => {
      state.modifiedPassword = action.payload;
    },
    setsendingForm: (state: AuthState, action: PayloadAction<boolean>) => {
      state.sendingForm = action.payload;
    },
    setPhoneVerificationLoading: (state: AuthState, action: PayloadAction<boolean>) => {
      state.phoneVerification.loading = action.payload;
    },
    setPhoneVerificationRequestLockedUntil: (state: AuthState, action: PayloadAction<number | undefined>) => {
      state.phoneVerification.requestLockedUntil = action.payload == undefined ? undefined : lockDateOn(action.payload);
    },
    setPhoneVerificationStatus: (state: AuthState, action: PayloadAction<PhoneVerificationStatus>) => {
      state.phoneVerification.status = action.payload;
    },
    setOtpLoading: (state: AuthState, action: PayloadAction<boolean>) => {
      state.otp.loading = action.payload;
    },
    setOtpRequestLockedUntil: (state: AuthState, action: PayloadAction<number | undefined>) => {
      state.otp.requestLockedUntil = action.payload == undefined ? undefined : lockDateOn(action.payload);
    },
    setOtpStatus: (state: AuthState, action: PayloadAction<PhoneVerificationStatus>) => {
      state.otp.status = action.payload;
    },
    setBuyerInfo: (state: AuthState, action: PayloadAction<Buyer>) => {
      state.buyer = action.payload;
    },
    setResetPasswordRequestLockedUntil: (state: AuthState, action: PayloadAction<number | undefined>) => {
      state.resetPassword.requestLockedUntil = action.payload == undefined ? undefined : lockDateOn(action.payload);
    },
    setResetPasswordLoading: (state: AuthState, action: PayloadAction<boolean>) => {
      state.resetPassword.loading = action.payload;
    },
    setResetPasswordCode: (state: AuthState, action: PayloadAction<string | undefined>) => {
      if (action.payload == undefined) {
        state.resetPassword.code = undefined;
        state.resetPassword.isCodeValid = false;
        return;
      }

      state.resetPassword.code = action.payload;
      state.resetPassword.isCodeValid = true;
    },
    resetResetPassword: (state: AuthState) => {
      state.resetPassword = {
        loading: false,
        isCodeValid: false,
        code: undefined,
        requestLockedUntil: undefined,
      };
    },
    setAppInfo: (state: AuthState, action: PayloadAction<AppInfo>) => {
      state.appInfo = action.payload;
    },
  },
});

export default authSlice.reducer;

export const {
  setPublicKey,
  setBuyer,
  clearBuyer,
  setTermsData,
  setErrors,
  setFlashMessage,
  ackFlashMessage,
  setPasswordRecoveryEmail,
  setPasswordRecoveryCode,
  setLogged,
  requestSignup,
  receiveSignup,
  setCurrentTerms,
  setCreatedAccount,
  setResendEmail,
  setFixEmail,
  setCodeResent,
  setmodifiedPassword,
  setsendingForm,
  setSetupAuthOngoing,
  setPhoneVerificationLoading,
  setPhoneVerificationRequestLockedUntil,
  setPhoneVerificationStatus,
  setBuyerInfo,
  setOtpLoading,
  setOtpRequestLockedUntil,
  setOtpStatus,
  setResetPasswordRequestLockedUntil,
  setResetPasswordLoading,
  setResetPasswordCode,
  resetResetPassword,
  setLoadingTerms,
  setAppInfo,
} = authSlice.actions;

const getUserToken = (state: RootState) => {
  const {
    auth: { publicApiKey, buyerToken },
  } = state;

  if (publicApiKey == null) {
    return undefined;
  }

  const userToken = storage.getValue(publicApiKey, planneWidgetBuyerKey);

  return userToken ?? buyerToken;
};

export const loadAppInfo = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const resp = await getClient(state).apps.getAppInfo();

  dispatch(setAppInfo(resp.data));
};

export const setupAuth =
  (persist?: boolean, token?: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    const userToken = token ?? getUserToken(state);

    dispatch(setSetupAuthOngoing(true));
    if (!userToken) {
      dispatch(setLogged(false));
      dispatch(setSetupAuthOngoing(false));
      return;
    }

    try {
      const resp = await getClient(state).buyers.getOwnBuyer(bearerAuth(userToken!));
      const buyer = resp.data;

      dispatch(registerBuyer(userToken!, buyer, false, persist));
    } catch (e) {
      dispatch(logoutBuyer());
    } finally {
      dispatch(setSetupAuthOngoing(false));
    }
  };

export const refreshBuyerInfo = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();

  const userToken = getUserToken(state);
  if (!userToken) {
    return;
  }

  try {
    const { data } = await getClient(state).buyers.getOwnBuyer(bearerAuth(userToken!));

    dispatch(setBuyerInfo(data));
  } catch (e) {
    dispatch(logoutBuyer());
  }
};

export const registerBuyer =
  (token: string, buyer: Buyer, isNewAccount = false, persist = true) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const publicApiKey = publicApiTokenSelector(getState());

    dispatch(setBuyer(token, buyer, isNewAccount));

    if (persist) {
      storage.setValue(publicApiKey, planneWidgetBuyerKey, token);
    }
  };

export const logoutBuyer = (redirectLocation?: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const publicApiKey = publicApiTokenSelector(state);

  dispatch(clearBuyer());
  storage.removeValue(publicApiKey, planneWidgetBuyerKey);

  const {
    cart: { cart },
  } = state;
  if (cart?.customerId) {
    dispatch(clearCartReference());
    dispatch(setupCart(undefined, true));
  }

  if (redirectLocation) {
    dispatch(changeLocation(redirectLocation));
  }
};

export const loadTerms = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const {
    auth: { termsData },
  } = state;

  if (termsData) {
    return;
  }

  dispatch(setLoadingTerms(true));

  const client = getClient(state);
  const { data: termsOfUseDoc } = await client.buyers.getTermsOfUse();
  const { data: privacyPolicyDoc } = await client.buyers.getPrivacyPolicy();

  dispatch(
    setTermsData({
      terms_of_use: termsOfUseDoc,
      privacy_policy: privacyPolicyDoc,
    }),
  );

  dispatch(setLoadingTerms(false));

  dispatch(setCurrentTerms(null));
};

export const login = (credentials: LoginRequest) => async (dispatch: AppDispatch, getState: () => RootState) => {
  dispatch(setsendingForm(true));
  dispatch(setErrors('login', {}));

  let response: AxiosResponse<Buyer>;
  try {
    response = await getClient(getState()).buyers.buyerLogin(credentials);
  } catch (err) {
    const errors = validationErrorsWithoutI18n(err);
    if (errors) {
      dispatch(setErrors('login', errors));

      track('loginFail', {
        errors,
      });
      return;
    } else if ((err as AxiosError).response?.status == 401) {
      dispatch(
        setErrors('login', {
          general: ['Credenciais inválidas'],
        }),
      );

      track('loginFail');
      return;
    } else {
      throw err;
    }
  } finally {
    dispatch(setsendingForm(false));
  }

  const { data: buyer, headers } = response!;
  const token = headers['x-token']!;

  track('loginSuccess');

  dispatch(registerBuyer(token, buyer));
};

export const signup = (credentials: SignupRequest) => async (dispatch: AppDispatch, getState: () => RootState) => {
  dispatch(requestSignup());
  dispatch(setErrors('signup', {}));
  dispatch(setsendingForm(true));

  let response: AxiosResponse<Buyer>;
  try {
    response = await getClient(getState()).buyers.buyerSignup(credentials);
  } catch (err) {
    const errors = validationErrorsWithoutI18n(err);

    track('signupFail', { errors });

    if (errors) {
      dispatch(setErrors('signup', errors));
      return;
    } else {
      throw err;
    }
  } finally {
    dispatch(setsendingForm(false));
    dispatch(receiveSignup());
  }

  track('signupSuccess');

  const { data: buyer, headers } = response!;
  const token = headers['x-token']!;

  dispatch(registerBuyer(token, buyer, true));
  dispatch(setCreatedAccount(true));
};

export const resetPassword =
  (credentials: ResetPasswordRequest) => async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setsendingForm(true));
    dispatch(setErrors('password_reset', {}));

    try {
      await getClient(getState()).buyers.resetPassword(credentials);
    } catch (err) {
      const errors = validationErrorsWithoutI18n(err);
      if (errors) {
        dispatch(setErrors('password_reset', errors));
        return;
      } else {
        throw err;
      }
    } finally {
      dispatch(setsendingForm(false));
    }

    dispatch(setErrors('password_recover', {}));
    dispatch(setPasswordRecoveryEmail(credentials.email));
  };

export const sendResetPasswordCode =
  (params: ResetPasswordRequest) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    if (!dateWasReached(resetPasswordSelector(state).requestLockedUntil)) {
      dispatch(setErrors('password_reset', { expiration: ['O código de verificação já foi enviado'] }));
      return;
    }

    dispatch(setResetPasswordLoading(true));
    dispatch(setErrors('password_reset', {}));

    try {
      await getClient(getState()).buyers.resetPassword(params);
    } catch (err) {
      if ((err as AxiosError).response?.status == 429) {
        dispatch(
          setErrors('phone_verification', {
            request: ['Não foi possível. Aguarde para solicitar um novo código'],
          }),
        );
        return;
      }

      const errors = validationErrorsWithoutI18n(err);
      if (errors) {
        dispatch(setErrors('password_reset', errors));
        return;
      } else {
        throw err;
      }
    } finally {
      dispatch(setResetPasswordLoading(false));

      dispatch(setResetPasswordRequestLockedUntil(1));
    }
  };

export const validateResetPasswordCode =
  (params: ValidatePasswordRecoveryCodeRequest) => async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setResetPasswordLoading(true));
    dispatch(setErrors('valid_code', {}));

    try {
      await getClient(getState()).buyers.validatePasswordRecoveryCode(params);

      dispatch(setResetPasswordCode(params.code));
    } catch (err) {
      if ((err as AxiosError).response?.status == 404) {
        dispatch(setErrors('valid_code', { code: ['Código Inválido'] }));
        return;
      }

      const errors = validationErrorsWithoutI18n(err);

      if (errors) {
        dispatch(setErrors('valid_code', errors));

        return;
      } else {
        throw err;
      }
    } finally {
      dispatch(setResetPasswordLoading(false));
    }
  };

export const validCode =
  (credentials: ValidatePasswordRecoveryCodeRequest) => async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setsendingForm(true));
    const passwordRecoveryCode = passwordRecoveryCodeSelector(getState());

    dispatch(setErrors('valid_code', {}));

    if (passwordRecoveryCode) {
      credentials = { ...credentials, code: passwordRecoveryCode };
    }

    try {
      await getClient(getState()).buyers.validatePasswordRecoveryCode(credentials);
    } catch (err) {
      if ((err as AxiosError).response?.status == 404) {
        dispatch(setErrors('valid_code', { code: ['Código Inválido'] }));
        return;
      }

      const errors = validationErrorsWithoutI18n(err);

      track('recoveryPasswordCodeStepError', { errors });

      if (errors) {
        dispatch(setErrors('valid_code', errors));

        return;
      } else {
        throw err;
      }
    } finally {
      dispatch(setsendingForm(false));
    }

    track('recoveryPasswordCodeStepSuccess');

    dispatch(setPasswordRecoveryCode(credentials.code));
  };

export const changePassword =
  (credentials: RecoverPasswordRequest) => async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setResetPasswordLoading(true));
    dispatch(setErrors('password_recover', {}));

    let response: AxiosResponse<Buyer>;
    try {
      response = await getClient(getState()).buyers.recoverPassword(credentials);
    } catch (err) {
      const errors = validationErrorsWithoutI18n(err);

      if (errors) {
        dispatch(setErrors('password_recover', errors));
        return;
      } else {
        throw err;
      }
    } finally {
      dispatch(setsendingForm(false));
      dispatch(setResetPasswordLoading(false));
    }

    const { data: buyer, headers } = response!;
    const token = headers['x-token']!;

    dispatch(registerBuyer(token, buyer));

    dispatch(setResetPasswordCode(undefined));

    dispatch(setResetPasswordLoading(false));
  };

export const recoverPassword =
  (credentials: RecoverPasswordRequest) => async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setsendingForm(true));
    dispatch(setErrors('password_recover', {}));

    let response: AxiosResponse<Buyer>;
    try {
      response = await getClient(getState()).buyers.recoverPassword(credentials);
    } catch (err) {
      const errors = validationErrorsWithoutI18n(err);

      track('recoveryPasswordRecoveryStepError', { errors });

      if (errors) {
        dispatch(setErrors('password_recover', errors));
        return;
      }

      throw err;
    } finally {
      dispatch(setsendingForm(false));
    }

    const { data: buyer, headers } = response!;
    const token = headers['x-token']!;

    track('recoveryPasswordRecoveryStepSuccess');

    dispatch(setmodifiedPassword(true));
    dispatch(registerBuyer(token, buyer));
    dispatch(setPasswordRecoveryEmail(undefined));
    dispatch(setPasswordRecoveryCode(undefined));
  };

export const confirmEmailResend = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const email = passwordRecoveryEmailSelector(getState());
  if (email) {
    dispatch(resetPassword({ email }));
    dispatch(setCodeResent(true));
    dispatch(setResendEmail(false));
  }
};

export const confirmEmailFix = () => async (dispatch: AppDispatch) => {
  dispatch(setPasswordRecoveryEmail(''));
  dispatch(setResendEmail(false));
  dispatch(setFixEmail(true));
};

export const requestPhoneVerification = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();

  if (!dateWasReached(phoneVerificationSelector(state).requestLockedUntil)) {
    dispatch(setErrors('phone_verification', { expiration: ['O código de verificação já foi enviado'] }));
    return;
  }

  const userToken = getUserToken(state);
  if (!userToken) {
    return;
  }

  dispatch(setPhoneVerificationLoading(true));
  dispatch(setErrors('phone_verification', {}));

  try {
    await getClient(state).buyers.requestPhoneVerification(bearerAuth(userToken!));
  } catch (err) {
    if ((err as AxiosError).response?.status == 429) {
      dispatch(
        setErrors('phone_verification', {
          request: ['Não foi possível. Aguarde para solicitar um novo código'],
        }),
      );
      return;
    }

    const errors = validationErrorsWithoutI18n(err);
    if (errors) {
      dispatch(setErrors('phone_verification', errors));
      return;
    } else {
      throw err;
    }
  } finally {
    dispatch(setPhoneVerificationLoading(false));

    dispatch(setPhoneVerificationRequestLockedUntil(1));
  }
};

export const verifyPhone = (params: VerifyPhoneRequest) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const userToken = getUserToken(state);
  if (!userToken) {
    return;
  }

  dispatch(setPhoneVerificationLoading(true));
  dispatch(setPhoneVerificationStatus('inProgress'));
  dispatch(setErrors('phone_verification', {}));

  try {
    await getClient(state).buyers.verifyPhone(params, bearerAuth(userToken!));

    dispatch(setPhoneVerificationStatus('success'));

    dispatch(refreshBuyerInfo());
  } catch (err) {
    const errors = validationErrorsWithoutI18n(err);
    if (errors) {
      dispatch(setErrors('phone_verification', errors));
      dispatch(setPhoneVerificationStatus('error'));
      return;
    } else {
      throw err;
    }
  } finally {
    dispatch(setPhoneVerificationLoading(false));
  }
};

export const requestOtp =
  (params: ResetPasswordRequest) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    if (!dateWasReached(otpSelector(state).requestLockedUntil)) {
      dispatch(setErrors('otp', { expiration: ['A chave de acesso já foi enviada'] }));
      return;
    }

    dispatch(setOtpLoading(true));
    dispatch(setErrors('phone_verification', {}));

    try {
      await getClient(state).buyers.sendOtp(params);
    } catch (err) {
      if ((err as AxiosError).response?.status == 429) {
        dispatch(
          setErrors('otp', {
            request: ['Não foi possível. Aguarde para solicitar uma nova chave'],
          }),
        );
        return;
      }

      const errors = validationErrorsWithoutI18n(err);
      if (errors) {
        dispatch(setErrors('otp', errors));
        return;
      } else {
        throw err;
      }
    } finally {
      dispatch(setOtpLoading(false));

      dispatch(setOtpRequestLockedUntil(1));
    }
  };

export const editUser = (params: UserRequest) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const userToken = getUserToken(state);
  if (!userToken) {
    return;
  }

  dispatch(setErrors('updateUser', {}));
  dispatch(setsendingForm(true));

  try {
    const { data } = await getClient(getState()).buyers.updateOwnBuyer(params, bearerAuth(userToken!));

    dispatch(setBuyerInfo(data));
  } catch (err) {
    const errors = validationErrorsWithoutI18n(err);

    track('updateUserFail', { errors });

    if (errors) {
      dispatch(setErrors('updateUser', errors));
      return;
    } else {
      throw err;
    }
  } finally {
    dispatch(setsendingForm(false));
    dispatch(receiveSignup());
  }

  track('updateUserSuccess');
};

export const publicApiTokenSelector = (state: RootState) => state.auth.publicApiKey!;
export const termsSelector = (state: RootState) => state.auth.termsData;
export const loadingTermsSelector = (state: RootState) => state.auth.loadingTerms;
export const currentTermsSelector = (state: RootState) => state.auth.currentTerms;
export const buyerSelector = (state: RootState): [Buyer | undefined, boolean | undefined] => [
  state.auth.buyer,
  state.auth.logged,
];
export const appInfoSelector = (state: RootState) => state.auth.appInfo;
export const errorsSelector = (state: RootState) => {
  const src = state.auth.errors;
  const dest: { [key: string]: { [key: string]: string[] } } = {};

  for (const key in src) {
    const value = src[key]!;
    const destValue: { [key: string]: string[] } = {};

    for (const key2 in value) {
      destValue[key2] = value[key2]!.map((errStr) => getErrorMessage(errStr));
    }

    dest[key] = destValue;
  }

  return dest;
};
export const hasEmailUniquenessErrorSelector = (state: RootState) =>
  state.auth.errors['signup']?.['email']?.includes('emailMustBeUnique') ?? false;
export const flashMessageSelector = (state: RootState) => state.auth.flashMessage;
export const passwordRecoveryEmailSelector = (state: RootState) => state.auth.passwordRecoveryEmail;
export const passwordRecoveryCodeSelector = (state: RootState) => state.auth.passwordRecoveryCode;
export const pendingSignupSelector = (state: RootState) => state.auth.pendingSignup;
export const createdAccountSelector = (state: RootState) => state.auth.createdAccount;
export const resendEmailSelector = (state: RootState) => state.auth.resendEmail;
export const fixEmailSelector = (state: RootState) => state.auth.fixEmail;
export const codeResentSelector = (state: RootState) => state.auth.codeResent;
export const modifiedPasswordSelector = (state: RootState) => state.auth.modifiedPassword;
export const sendingFormSelector = (state: RootState) => state.auth.sendingForm;
export const setupAuthOngoingSelector = (state: RootState) => state.auth.setupAuthOngoing;
export const phoneVerificationSelector = (state: RootState) => state.auth.phoneVerification;
export const otpSelector = (state: RootState) => state.auth.otp;
export const authSelector = (state: RootState) => state.auth;
export const resetPasswordSelector = (state: RootState) => state.auth.resetPassword;

export const buyerIsForeign = ({ auth: { buyer } }: RootState) => buyer != null && buyer.countryCode !== 'BRA';
export const buyerNeedsPhoneVerificationSelector = () => false;

export const featureFlagsSelector = () => (state: RootState) => state.auth.appInfo?.featureFlags;
export const featureFlagSelector = (flag: string) => (state: RootState) =>
  state.auth.appInfo?.featureFlags?.[flag] === true;
